
/**
 * Module dependencies.
 */

import { MenuLink } from 'src/core/menu';
import { units } from 'src/styles/utils';
import FooterLink from './footer-link';
import React, { ReactElement } from 'react';
import Svg from 'src/components/core/svg';
import map from 'lodash/map';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  className?: string,
  links: MenuLink[]
};

/**
 * `List` styled component.
 */

const List = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0;
  padding: 0;
`;

/**
 * `IconLink` styled component.
 */

const IconLink = styled(FooterLink)`
  align-items: center;
  display: flex;
  gap: ${units(1)};
`;

/**
 * `Icon` styled component.
 *
 * The icons draw their glyph inside a padded square, so the negative margins
 * keep the row as tall as a plain link and line the glyph up with the column.
 */

const Icon = styled(Svg)`
  flex: none;
  margin: -${units(0.5)} 0 -${units(0.5)} -${units(1)};
`;

/**
 * `FooterLinkList` component.
 *
 * A link with an `icon`, like the social networks, shows it before its label.
 */

const FooterLinkList = ({ className, links }: Props): ReactElement => (
  <List className={className}>
    {map(links, ({ href, icon, label }: MenuLink) => (
      <li key={href}>
        {icon ? (
          <IconLink href={href}>
            <Icon
              aria-hidden
              icon={icon}
              size={'32px'}
            />

            {label}
          </IconLink>
        ) : (
          <FooterLink href={href}>
            {label}
          </FooterLink>
        )}
      </li>
    ))}
  </List>
);

/**
 * Export `FooterLinkList` component.
 */

export default FooterLinkList;
